import { useState, useMemo, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { usePlivo } from "@/lib/plivo-context";
import { useAuth } from "@/lib/auth-context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountryPhoneInput } from "@/components/country-phone-input";
import { AlertTriangle, Loader2, Send, MessageSquare, Mail, Star, FileText, X } from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import { SearchableSelect } from "@/components/searchable-select";
import { CannedResponseSelector } from "@/components/canned-response-selector";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import type { Disposition, TenantBusinessSettings, Contact } from "@shared/schema";
import type { CsatTemplateOption } from "@shared/csat-template-options";

type DispositionSettings = {
  dispositionEnabled: boolean;
  forceDisposition: boolean;
};

const AUTO_TEMPLATE = "__auto__";

export function PostCallDisposition() {
  const { pendingDispositionCallLogId, pendingDispositionContactId, clearPendingDisposition, campaignDispositionConfig, setCampaignDispositionConfig } = usePlivo();
  const { user } = useAuth();
  const { toast } = useToast();
  const csatTemplateTenantId = user?.impersonating?.tenantId || user?.tenantId;

  const [selectedParentId, setSelectedParentId] = useState("");
  const [selectedChildId, setSelectedChildId] = useState("");
  const [notes, setNotes] = useState("");
  const [csatSending, setCsatSending] = useState(false);
  const [showCsatPicker, setShowCsatPicker] = useState(false);
  const [pickerTemplateId, setPickerTemplateId] = useState(AUTO_TEMPLATE);

  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const [smsFromNumber, setSmsFromNumber] = useState("");

  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waMessage, setWaMessage] = useState("");
  const [waFromNumber, setWaFromNumber] = useState("");

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailBcc, setEmailBcc] = useState("");
  const [showBcc, setShowBcc] = useState(false);
  const [emailFromOption, setEmailFromOption] = useState("auto");
  const [emailSentForDisposition, setEmailSentForDisposition] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [emailConfirmPayload, setEmailConfirmPayload] = useState<{ toEmail: string; subject: string; body: string; cc?: string; bcc?: string; fromEmail?: string; inboxId?: string } | null>(null);
  const emailEditorRef = useRef<RichTextEditorHandle>(null);

  const { data: dispositionsRaw } = useQuery<Disposition[]>({
    queryKey: ["/api/dispositions", { context: "call" }],
    queryFn: async () => {
      const res = await fetch("/api/dispositions?context=call", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const json = await res.json();
      return Array.isArray(json) ? json : (json?.data ?? []);
    },
  });
  const dispositions = dispositionsRaw ?? [];

  const { data: dispoSettings } = useQuery<DispositionSettings>({
    queryKey: ["/api/disposition-settings"],
  });

  const { data: settingsWrapper } = useQuery<{ tenantBusinessSettings: TenantBusinessSettings }>({
    queryKey: ["/api/tenant-settings"],
  });
  const businessSettings = settingsWrapper?.tenantBusinessSettings;

  const { data: callLogData } = useQuery<{ direction?: string }>({
    queryKey: ["/api/call-logs", pendingDispositionCallLogId],
    enabled: !!pendingDispositionCallLogId,
  });
  const isInboundCall = callLogData?.direction === "inbound";

  const csatOutboundEnabled = !!(businessSettings as any)?.csatOutboundEnabled;
  const csatManualEnabled = (businessSettings as any)?.csatManualEnabled !== false;
  const csatEligible = !!(businessSettings?.csatEnabled && csatManualEnabled && (isInboundCall || csatOutboundEnabled));

  const {
    data: csatTemplatesData,
    isLoading: csatTemplatesLoading,
    isError: csatTemplatesError,
  } = useQuery<CsatTemplateOption[]>({
    queryKey: ["/api/csat/template-options", csatTemplateTenantId],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/csat/template-options");
      return response.json() as Promise<CsatTemplateOption[]>;
    },
    enabled: csatEligible && !!csatTemplateTenantId,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const csatTemplates = csatTemplatesData ?? [];

  const { data: contact } = useQuery<Contact>({
    queryKey: ["/api/contacts", pendingDispositionContactId],
    enabled: !!pendingDispositionContactId,
  });
  const contactEmail = contact?.email || "";

  const { data: waEnabledData } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/whatsapp/enabled"],
  });
  const waEnabled = waEnabledData?.enabled === true;

  const { data: waNumberData } = useQuery<{ number: string | null }>({
    queryKey: ["/api/whatsapp/number"],
    enabled: waEnabled,
  });
  const waNumber = waNumberData?.number || null;

  const { data: waTemplatesData } = useQuery<Array<{ id: string; name: string; body: string; category?: string | null; isActive?: boolean | null }>>({
    queryKey: ["/api/whatsapp/templates"],
    enabled: waEnabled,
  });
  const waTemplates = waTemplatesData ?? [];
  const activeWaTemplates = waTemplates.filter(t => t.isActive !== false);

  const [waTemplateOpen, setWaTemplateOpen] = useState(false);

  const { data: allPhoneNumbersData } = useQuery<Array<{ id: string; number: string; friendlyName: string | null; smsEnabled?: boolean }>>({
    queryKey: ["/api/phone-numbers"],
  });
  const allPhoneNumbers = allPhoneNumbersData ?? [];
  const smsPhoneNumbers = allPhoneNumbers.filter(pn => pn.smsEnabled !== false);

  const { data: ticketInboxesData } = useQuery<Array<{ id: string; name: string; email: string; emailAddress?: string; isActive?: boolean; signatureConfirmed?: boolean }>>({
    queryKey: ["/api/ticket-inboxes"],
  });
  const ticketInboxes = ticketInboxesData ?? [];
  const confirmedInboxes = ticketInboxes.filter(i => i.isActive !== false && i.signatureConfirmed !== false);

  const { data: agentSettings } = useQuery<{ emailSenderAddress?: string | null; emailSignatureConfirmed?: boolean; smsAssignedNumberId?: string | null }>({
    queryKey: ["/api/user-settings"],
  });
  const agentEmailVerified = agentSettings?.emailSenderAddress && agentSettings?.emailSignatureConfirmed;

  useEffect(() => {
    if (agentSettings?.smsAssignedNumberId && !smsFromNumber && smsPhoneNumbers.length > 0) {
      const defaultPhone = smsPhoneNumbers.find(pn => pn.id === agentSettings.smsAssignedNumberId);
      if (defaultPhone) setSmsFromNumber(defaultPhone.number);
    }
  }, [agentSettings?.smsAssignedNumberId, smsPhoneNumbers, smsFromNumber]);

  useEffect(() => {
    if (waNumber && !waFromNumber) {
      setWaFromNumber(waNumber);
    }
  }, [waNumber, waFromNumber]);

  // Pre-fill the "To" field whenever the email dialog opens
  useEffect(() => {
    if (emailDialogOpen) {
      setEmailTo(contactEmail || "");
    }
  }, [emailDialogOpen]);

  const isEnabled = dispoSettings?.dispositionEnabled ?? false;
  const isForced = campaignDispositionConfig?.forceDisposition ?? (dispoSettings?.forceDisposition ?? false);
  const isOpen = !!campaignDispositionConfig || (!!pendingDispositionCallLogId && isEnabled);

  useEffect(() => {
    if (pendingDispositionCallLogId && dispoSettings !== undefined && !isEnabled && !campaignDispositionConfig) {
      clearPendingDisposition();
    }
  }, [pendingDispositionCallLogId, isEnabled, dispoSettings, campaignDispositionConfig]);

  const parentDispositions = useMemo(() => {
    return dispositions
      .filter((d) => {
        if (d.parentId) return false;
        const dir = (d as any).callDirection || "both";
        if (dir === "inbound") return isInboundCall;
        if (dir === "outbound") return !isInboundCall;
        return true;
      })
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [dispositions, isInboundCall]);

  const childrenOfSelected = useMemo(() => {
    if (!selectedParentId) return [];
    return dispositions
      .filter((d) => {
        if (d.parentId !== selectedParentId) return false;
        const dir = (d as any).callDirection || "both";
        if (dir === "inbound") return isInboundCall;
        if (dir === "outbound") return !isInboundCall;
        return true;
      })
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [dispositions, selectedParentId, isInboundCall]);

  const getDispositionString = (): string => {
    if (!selectedParentId) return "";
    const parent = parentDispositions.find((d) => d.id === selectedParentId);
    if (!parent) return "";
    if (selectedChildId) {
      const child = childrenOfSelected.find((d) => d.id === selectedChildId);
      if (child) return `${parent.name} > ${child.name}`;
    }
    return parent.name;
  };

  const hasDisposition = !!selectedParentId;

  const requiresEmail = useMemo(() => {
    if (!selectedParentId) return false;
    if (selectedChildId) {
      const child = childrenOfSelected.find((d) => d.id === selectedChildId);
      if (child) return !!(child as any).forceEmail;
    }
    const parent = parentDispositions.find((d) => d.id === selectedParentId);
    return !!(parent as any)?.forceEmail;
  }, [selectedParentId, selectedChildId, parentDispositions, childrenOfSelected]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const dispositionStr = getDispositionString();
      const updates: Record<string, any> = {};
      if (dispositionStr) updates.disposition = dispositionStr;
      if (notes.trim()) {
        updates.notes = notes.trim();
        updates.dispositionNote = notes.trim();
      }

      if (pendingDispositionContactId) updates.contactId = pendingDispositionContactId;

      if (Object.keys(updates).length > 0 && pendingDispositionCallLogId) {
        try {
          await apiRequest("PATCH", `/api/call-logs/${pendingDispositionCallLogId}`, updates);
        } catch (e) {
          console.error("Failed to update call log disposition:", e);
        }
      }

      if (campaignDispositionConfig && dispositionStr) {
        try {
          await apiRequest("POST", `/api/preview-dialer/campaigns/${campaignDispositionConfig.campaignId}/disposition`, {
            campaignContactId: campaignDispositionConfig.campaignContactId,
            disposition: dispositionStr,
            notes: notes.trim() || undefined,
          });
        } catch (e) {
          console.error("Failed to save preview dialer campaign disposition:", e);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/call-logs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/disposition-settings"] });
      // Use pendingDispositionContactId when available; fall back to the contactId embedded
      // in campaignDispositionConfig for preview-dialer calls where the Plivo async
      // onCallTerminated may not have resolved yet when the agent submits the disposition.
      const resolvedContactId = pendingDispositionContactId || campaignDispositionConfig?.contactId || null;
      if (resolvedContactId) {
        queryClient.invalidateQueries({ queryKey: ["/api/contacts", resolvedContactId] });
        queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
        queryClient.invalidateQueries({ queryKey: ["/api/contacts", resolvedContactId, "disposition-events"] });
      }
      if (campaignDispositionConfig) {
        queryClient.invalidateQueries({ queryKey: ["/api/preview-dialer/campaigns", campaignDispositionConfig.campaignId, "stats"] });
      }
      if (businessSettings?.csatEnabled && businessSettings?.csatAutoTrigger && pendingDispositionCallLogId && csatEligible) {
        triggerCsatSurvey("auto");
      }
      handleClose();
    },
    onError: (error: any) => {
      console.error("Failed to save disposition:", error);
      handleClose();
    },
  });

  const smsMutation = useMutation({
    mutationFn: async (data: { channel: string; toAddress: string; fromAddress: string; body: string }) => {
      const res = await apiRequest("POST", "/api/conversations", data);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "SMS sent successfully" });
      setSmsDialogOpen(false);
      setSmsMessage("");
    },
    onError: (error: Error) => {
      toast({ title: "SMS failed", description: error.message, variant: "destructive" });
    },
  });

  const waMutation = useMutation({
    mutationFn: async (data: { channel: string; toAddress: string; fromAddress: string; body: string }) => {
      const res = await apiRequest("POST", "/api/conversations", data);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "WhatsApp message sent" });
      setWaDialogOpen(false);
      setWaMessage("");
    },
    onError: (error: Error) => {
      toast({ title: "WhatsApp failed", description: error.message, variant: "destructive" });
    },
  });

  const emailMutation = useMutation({
    mutationFn: async (data: { toEmail: string; subject: string; body: string; cc?: string; bcc?: string; fromEmail?: string; inboxId?: string }) => {
      return apiRequest("POST", "/api/tickets/compose", data);
    },
    onSuccess: () => {
      toast({ title: "Email sent successfully" });
      setEmailConfirmOpen(false);
      setEmailConfirmPayload(null);
      setEmailDialogOpen(false);
      setEmailTo("");
      setEmailSubject("");
      setEmailBody("");
      setEmailCc("");
      setEmailBcc("");
      setShowBcc(false);
      setEmailFromOption("auto");
      setEmailSentForDisposition(true);
    },
    onError: (error: Error) => {
      setEmailConfirmOpen(false);
      toast({ title: "Email failed", description: error.message, variant: "destructive" });
    },
  });

  const triggerCsatSurvey = async (
    triggerType: "auto" | "manual",
    overrideCooldown = false,
    surveyTemplateId?: string,
  ) => {
    if (!pendingDispositionCallLogId) return;
    setCsatSending(true);
    try {
      const callResp = await fetch(`/api/call-logs/${pendingDispositionCallLogId}`, { credentials: "include" });
      if (!callResp.ok) throw new Error("Could not fetch call log");
      const callLog = await callResp.json();
      const customerNumber = callLog.customerNumber
        || (callLog.direction === "outbound" ? callLog.toNumber : callLog.fromNumber)
        || callLog.fromNumber
        || callLog.toNumber;
      if (!customerNumber) throw new Error("No customer number found");

      if (triggerType === "manual" && !overrideCooldown) {
        const cooldownResp = await fetch(`/api/csat/cooldown-check?phoneNumber=${encodeURIComponent(customerNumber)}`, { credentials: "include" });
        if (cooldownResp.ok) {
          const cooldownData = await cooldownResp.json();
          if (!cooldownData.eligible && cooldownData.reason === "cooldown") {
            const override = window.confirm(
              "This customer was recently surveyed and is in the cooldown period. Send the survey anyway?"
            );
            if (!override) {
              toast({ title: "CSAT survey cancelled" });
              setCsatSending(false);
              return;
            }
            overrideCooldown = true;
          } else if (!cooldownData.eligible) {
            toast({ title: "CSAT survey unavailable", description: `Reason: ${cooldownData.reason}` });
            setCsatSending(false);
            return;
          }
        }
      }

      const body: Record<string, any> = {
        callId: pendingDispositionCallLogId,
        triggerType,
        overrideCooldown,
      };
      if (surveyTemplateId) body.surveyTemplateId = surveyTemplateId;

      const resp = await apiRequest("POST", "/api/csat/trigger", body);
      const data = await resp.json();
      if (data.skipped) {
        toast({ title: "CSAT survey skipped", description: `Reason: ${data.reason}` });
      } else {
        toast({ title: "CSAT survey sent", description: "Survey call initiated successfully" });
      }
    } catch (err: any) {
      console.error("[CSAT] Failed to trigger survey:", err);
      if (triggerType === "manual") {
        toast({ title: "Failed to send CSAT survey", description: err.message, variant: "destructive" });
      }
    } finally {
      setCsatSending(false);
    }
  };

  const handleClose = () => {
    setSelectedParentId("");
    setSelectedChildId("");
    setNotes("");
    setShowCsatPicker(false);
    setPickerTemplateId(AUTO_TEMPLATE);
    setSmsDialogOpen(false);
    setSmsMessage("");
    setWaDialogOpen(false);
    setWaMessage("");
    setEmailDialogOpen(false);
    setEmailTo("");
    setEmailSubject("");
    setEmailBody("");
    setEmailCc("");
    setEmailBcc("");
    setShowBcc(false);
    setEmailFromOption("auto");
    setEmailSentForDisposition(false);
    const onDone = campaignDispositionConfig?.onDone;
    setCampaignDispositionConfig(null);
    clearPendingDisposition();
    onDone?.();
  };

  const handleSave = () => {
    saveMutation.mutate();
  };

  const handleParentChange = (val: string) => {
    setSelectedParentId(val);
    setSelectedChildId("");
    setEmailSentForDisposition(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isForced) handleClose();
  };

  const handleCsatPickerConfirm = () => {
    setShowCsatPicker(false);
    const templateId = pickerTemplateId === AUTO_TEMPLATE ? undefined : pickerTemplateId;
    triggerCsatSurvey("manual", false, templateId);
    setPickerTemplateId(AUTO_TEMPLATE);
  };

  if (!isOpen) return null;

  const contactName = contact?.name || "";
  const contactPhone = contact?.phoneNumber || "";
  const isSubDialogOpen = smsDialogOpen || waDialogOpen || emailDialogOpen || showCsatPicker;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={false}>
        <DialogContent
          className="sm:max-w-lg shadow-2xl transition-opacity duration-150"
          style={isSubDialogOpen ? { opacity: 0, pointerEvents: 'none' } : undefined}
          data-testid="dialog-post-call-disposition"
        >
          <div>
          <DialogHeader>
            <DialogTitle>Post-Call Disposition</DialogTitle>
            {isForced && (
              <DialogDescription className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                Disposition is required before closing
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Disposition</label>
              <SearchableSelect
                options={parentDispositions.map((d) => ({ value: d.id, label: d.name }))}
                value={selectedParentId}
                onValueChange={handleParentChange}
                placeholder="Select disposition..."
                searchPlaceholder="Search dispositions..."
                data-testid="select-disposition-parent"
              />

              {childrenOfSelected.length > 0 && (
                <SearchableSelect
                  options={childrenOfSelected.map((d) => ({ value: d.id, label: d.name }))}
                  value={selectedChildId}
                  onValueChange={(v) => { setSelectedChildId(v); setEmailSentForDisposition(false); }}
                  placeholder="Select sub-disposition..."
                  searchPlaceholder="Search sub-dispositions..."
                  data-testid="select-disposition-child"
                />
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Add notes about this call..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="resize-none"
                rows={3}
                data-testid="textarea-post-call-notes"
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col items-stretch mt-4">
            <div className="flex flex-wrap gap-2">
              {csatEligible && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setPickerTemplateId(AUTO_TEMPLATE);
                    setShowCsatPicker(true);
                  }}
                  disabled={csatSending || saveMutation.isPending}
                  data-testid="button-send-csat-survey"
                >
                  {csatSending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Star className="w-4 h-4 mr-1.5" />}
                  {csatSending ? "Sending..." : "CSAT"}
                </Button>
              )}
              {waEnabled && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!contactPhone) {
                      toast({ title: "No phone number on file for this contact", description: "Add a phone number to the contact first." });
                      return;
                    }
                    setWaDialogOpen(true);
                  }}
                  disabled={saveMutation.isPending}
                  data-testid="button-send-wa"
                >
                  <SiWhatsapp className="w-4 h-4 mr-1.5" />
                  WhatsApp
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  if (!contactPhone) {
                    toast({ title: "No phone number on file for this contact", description: "Add a phone number to the contact first." });
                    return;
                  }
                  setSmsDialogOpen(true);
                }}
                disabled={saveMutation.isPending}
                data-testid="button-send-sms"
              >
                <MessageSquare className="w-4 h-4 mr-1.5" />
                SMS
              </Button>
              <Button
                variant={requiresEmail && !emailSentForDisposition ? "default" : "outline"}
                onClick={() => setEmailDialogOpen(true)}
                disabled={saveMutation.isPending}
                data-testid="button-send-email"
                className={requiresEmail && !emailSentForDisposition ? "animate-pulse" : ""}
              >
                <Mail className="w-4 h-4 mr-1.5" />
                Email{requiresEmail && !emailSentForDisposition ? " (required)" : ""}
              </Button>
            </div>

            {requiresEmail && !emailSentForDisposition && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Mail className="w-3 h-3 shrink-0" />
                This disposition requires sending an email before you can save.
              </p>
            )}

            <div className="flex gap-2 justify-end">
              {!isForced && (
                <Button variant="outline" onClick={handleClose} data-testid="button-skip-disposition">
                  Skip
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending || (isForced && !hasDisposition) || (requiresEmail && !emailSentForDisposition)}
                data-testid="button-save-disposition"
              >
                {saveMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {showCsatPicker && (
        <Dialog open={showCsatPicker} onOpenChange={(open) => { if (!open) { setShowCsatPicker(false); setPickerTemplateId(AUTO_TEMPLATE); } }}>
          <DialogContent className="sm:max-w-sm" style={{ zIndex: 300 }} overlayStyle={{ zIndex: 299 }} data-testid="dialog-csat-template-picker">
            <DialogHeader>
              <DialogTitle>Send CSAT Survey</DialogTitle>
              <DialogDescription>
                Choose which survey to send. The system picks automatically based on the call's queue — override only if the caller reached the wrong queue or number.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-1">
              <label className="text-sm font-medium">Survey Template</label>
              <Select
                value={pickerTemplateId}
                onValueChange={setPickerTemplateId}
              >
                <SelectTrigger data-testid="select-csat-template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[400]">
                  <SelectItem value={AUTO_TEMPLATE} data-testid="option-csat-template-auto">
                    Auto (queue default)
                  </SelectItem>
                  {csatTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id} data-testid={`option-csat-template-${template.id}`}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {csatTemplatesLoading && (
                <p className="text-xs text-muted-foreground" role="status">Loading survey templates...</p>
              )}
              {csatTemplatesError && (
                <p className="text-xs text-destructive" role="alert">Unable to load survey templates. Auto selection is still available.</p>
              )}
              {!csatTemplatesLoading && !csatTemplatesError && csatTemplates.length === 0 && (
                <p className="text-xs text-muted-foreground">No active survey templates are available. Auto selection will be used.</p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowCsatPicker(false); setPickerTemplateId(AUTO_TEMPLATE); }}
                data-testid="button-csat-picker-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCsatPickerConfirm}
                disabled={csatSending}
                data-testid="button-csat-picker-confirm"
              >
                Send Survey
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={smsDialogOpen} onOpenChange={(open) => { if (!open) { setSmsDialogOpen(false); setSmsMessage(""); } }}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col" style={{ zIndex: 300 }} overlayStyle={{ zIndex: 299 }} data-testid="dialog-sms">
          <DialogHeader className="shrink-0">
            <DialogTitle>Send SMS to {contactName || contactPhone}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
            <div>
              <Label>To (Phone Number)</Label>
              <CountryPhoneInput
                value={contactPhone}
                onChange={() => {}}
                defaultCountryCode={(settingsWrapper as any)?.defaultCountryCode}
                disabled
                inputTestId="input-sms-to"
                countryTestId="button-sms-country"
              />
            </div>
            <div>
              <Label>From (Your Number)</Label>
              <SearchableSelect
                options={smsPhoneNumbers.map(pn => ({
                  value: pn.number,
                  label: `${pn.number}${pn.friendlyName ? ` - ${pn.friendlyName}` : ""}`,
                }))}
                value={smsFromNumber}
                onValueChange={setSmsFromNumber}
                placeholder="Select number"
                searchPlaceholder="Search numbers..."
                data-testid="select-sms-from"
              />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                <Label>Message</Label>
                <CannedResponseSelector
                  channel="sms"
                  insideDialog
                  contact={contact || undefined}
                  onSelect={(text) => setSmsMessage((prev) => prev ? prev + text : text)}
                />
              </div>
              <Textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                data-testid="input-sms-message"
              />
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => { setSmsDialogOpen(false); setSmsMessage(""); }} data-testid="button-sms-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!contactPhone || !smsMessage.trim() || !smsFromNumber) return;
                smsMutation.mutate({ channel: "sms", toAddress: contactPhone, fromAddress: smsFromNumber, body: smsMessage.trim() });
              }}
              disabled={!smsMessage.trim() || !smsFromNumber || smsMutation.isPending}
              data-testid="button-sms-send"
            >
              {smsMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={waDialogOpen} onOpenChange={(open) => { if (!open) { setWaDialogOpen(false); setWaMessage(""); setWaTemplateOpen(false); } }}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col" style={{ zIndex: 300 }} overlayStyle={{ zIndex: 299 }} data-testid="dialog-whatsapp">
          <DialogHeader className="shrink-0">
            <DialogTitle>Send WhatsApp to {contactName || contactPhone}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
            <div>
              <Label>To (Phone Number)</Label>
              <Input value={contactPhone} disabled data-testid="input-wa-to" />
            </div>
            <div>
              <Label>From (Your Number)</Label>
              {waNumber ? (
                <Input value={waNumber} disabled data-testid="input-wa-from" />
              ) : (
                <Input value="No WhatsApp number configured" disabled className="text-muted-foreground" data-testid="input-wa-from" />
              )}
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                <Label>Message</Label>
                <div className="relative">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setWaTemplateOpen(prev => !prev)}
                    data-testid="button-wa-templates"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    Select Templates
                  </Button>
                  {waTemplateOpen && (
                    <div className="absolute right-0 top-full mt-1 w-72 rounded-md border bg-popover text-popover-foreground shadow-lg" style={{ zIndex: 200 }} data-testid="panel-wa-templates">
                      <div className="flex items-center justify-between gap-2 p-2 border-b">
                        <span className="text-sm font-medium">WhatsApp Templates</span>
                        <button
                          onClick={() => setWaTemplateOpen(false)}
                          className="p-1 rounded-md text-muted-foreground hover:text-foreground"
                          data-testid="button-close-wa-templates"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {activeWaTemplates.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-3">No approved templates found.</p>
                      ) : (
                        <div className="max-h-48 overflow-y-auto">
                          {activeWaTemplates.map(t => (
                            <button
                              key={t.id}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b last:border-0"
                              onClick={() => {
                                setWaMessage(t.body);
                                setWaTemplateOpen(false);
                              }}
                              data-testid={`button-wa-template-${t.id}`}
                            >
                              <p className="font-medium truncate">{t.name}</p>
                              <p className="text-muted-foreground text-xs truncate">{t.body}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <Textarea
                value={waMessage}
                onChange={(e) => setWaMessage(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                data-testid="input-wa-message"
              />
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => { setWaDialogOpen(false); setWaMessage(""); }} data-testid="button-wa-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!contactPhone || !waMessage.trim() || !waFromNumber) return;
                waMutation.mutate({ channel: "whatsapp", toAddress: contactPhone, fromAddress: waFromNumber, body: waMessage.trim() });
              }}
              disabled={!waMessage.trim() || !waFromNumber || waMutation.isPending}
              data-testid="button-wa-send"
            >
              {waMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailDialogOpen} onOpenChange={(open) => { if (!open && !emailConfirmOpen) { setEmailDialogOpen(false); setEmailTo(""); setEmailSubject(""); setEmailBody(""); setEmailCc(""); setEmailBcc(""); setShowBcc(false); setEmailFromOption("auto"); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" style={{ zIndex: 300 }} overlayStyle={{ zIndex: 299 }} onCloseAutoFocus={(e) => e.preventDefault()} data-testid="dialog-email">
          <DialogHeader className="shrink-0">
            <DialogTitle>Send Email{contactName ? ` to ${contactName}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
            <div>
              <Label>To</Label>
              <Input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="recipient@example.com"
                type="email"
                data-testid="input-email-to"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>CC <span className="text-muted-foreground font-normal">(optional)</span></Label>
                {!showBcc && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    onClick={() => setShowBcc(true)}
                    data-testid="button-add-bcc"
                  >
                    Add BCC
                  </button>
                )}
              </div>
              <Input
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                placeholder="cc@example.com, another@example.com"
                data-testid="input-email-cc"
              />
            </div>
            {showBcc && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>BCC <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    onClick={() => { setShowBcc(false); setEmailBcc(""); }}
                    data-testid="button-remove-bcc"
                  >
                    Remove
                  </button>
                </div>
                <Input
                  value={emailBcc}
                  onChange={(e) => setEmailBcc(e.target.value)}
                  placeholder="bcc@example.com, another@example.com"
                  autoFocus
                  data-testid="input-email-bcc"
                />
              </div>
            )}
            <div>
              <Label>From <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <SearchableSelect
                options={[
                  { value: "auto", label: "Auto (first available inbox)" },
                  ...confirmedInboxes.map(inbox => ({
                    value: inbox.id,
                    label: `${inbox.name} (${inbox.email || inbox.emailAddress})`,
                  })),
                  ...(agentEmailVerified ? [{ value: "agent", label: `My email (${agentSettings?.emailSenderAddress})` }] : []),
                ]}
                value={emailFromOption}
                onValueChange={setEmailFromOption}
                placeholder="Auto-select"
                searchPlaceholder="Search inboxes..."
                data-testid="select-email-from"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject..."
                data-testid="input-email-subject"
              />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                <Label>Message</Label>
                <CannedResponseSelector
                  channel="ticket"
                  insideDialog
                  contact={contact || undefined}
                  onSelect={(text) => emailEditorRef.current?.insertHTML(text)}
                  onSelectCcBcc={(newCc, newBcc) => {
                    if (newCc) setEmailCc(newCc);
                    if (newBcc) { setEmailBcc(newBcc); setShowBcc(true); }
                  }}
                  onSelectSubjectInbox={(newSubject, newInboxId) => {
                    if (newSubject) setEmailSubject(newSubject);
                    if (newInboxId) setEmailFromOption(newInboxId);
                  }}
                />
              </div>
              <RichTextEditor
                ref={emailEditorRef}
                key={emailDialogOpen ? "open" : "closed"}
                value={emailBody}
                onChange={setEmailBody}
                placeholder="Type your email..."
                minHeight={120}
                maxHeight={220}
                data-testid="input-email-body"
              />
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => { setEmailDialogOpen(false); setEmailTo(""); setEmailSubject(""); setEmailBody(""); setEmailCc(""); setEmailBcc(""); setShowBcc(false); setEmailFromOption("auto"); }} data-testid="button-email-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                const bodyText = emailBody.replace(/<[^>]*>/g, "").trim();
                if (!emailTo.trim() || !bodyText) return;
                const payload: { toEmail: string; subject: string; body: string; cc?: string; bcc?: string; fromEmail?: string; inboxId?: string } = {
                  toEmail: emailTo.trim(),
                  subject: emailSubject.trim(),
                  body: emailBody,
                };
                if (emailCc.trim()) payload.cc = emailCc.trim();
                if (emailBcc.trim()) payload.bcc = emailBcc.trim();
                if (emailFromOption === "agent" && agentSettings?.emailSenderAddress) {
                  payload.fromEmail = agentSettings.emailSenderAddress;
                } else if (emailFromOption !== "auto") {
                  payload.inboxId = emailFromOption;
                }
                setEmailConfirmPayload(payload);
                setEmailConfirmOpen(true);
              }}
              disabled={!emailTo.trim() || !emailBody.replace(/<[^>]*>/g, "").trim() || emailMutation.isPending}
              data-testid="button-email-send"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailConfirmOpen} onOpenChange={(open) => { if (!open) setEmailConfirmOpen(false); }}>
        <DialogContent className="max-w-sm" style={{ zIndex: 400 }} overlayStyle={{ zIndex: 399 }} onCloseAutoFocus={(e) => e.preventDefault()} data-testid="dialog-email-confirm">
          <DialogHeader>
            <DialogTitle>Confirm Send</DialogTitle>
            <DialogDescription>
              You are about to send an email to:
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm font-medium break-all" data-testid="text-email-confirm-to">
              {emailConfirmPayload?.toEmail}
            </div>
            {emailConfirmPayload?.cc && (
              <p className="text-xs text-muted-foreground mt-1.5">CC: {emailConfirmPayload.cc}</p>
            )}
            {emailConfirmPayload?.bcc && (
              <p className="text-xs text-muted-foreground mt-0.5">BCC: {emailConfirmPayload.bcc}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailConfirmOpen(false)} disabled={emailMutation.isPending} data-testid="button-email-confirm-cancel">
              Go Back
            </Button>
            <Button
              onClick={() => { if (emailConfirmPayload) emailMutation.mutate(emailConfirmPayload); }}
              disabled={emailMutation.isPending}
              data-testid="button-email-confirm-send"
            >
              {emailMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {emailMutation.isPending ? "Sending..." : "Confirm Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
